import React, { useMemo } from 'react';
import { GetAppointmentOutput } from 'graphql/generated/hasura';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { useGetEncounterSignedUrlFromStorageFunction } from 'hooks/useGetSignedUrlFromStorage';
import { useAuth } from 'auth/context/AuthContext';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import {
  downloadFileFromUrl,
  formatFileSize,
  mapApiFileType,
} from 'utilities/fileHelpers';
import Loader from 'components/loaderComponent';
import { FileItem, FileItemType } from '../complete-my-visit-summary/FileItem';
import moment from 'moment/moment';
import { STANDARD_DATE } from '../../../../utilities/dateHelper';
import { twMerge } from 'tailwind-merge';
import { Card } from '../Card';
import { Roles } from '../../../../firebase/interfaces';
import Alert from '../../../../components/alertComponent';

type GetAppointmentStatuses = Omit<GetAppointmentOutput, 'recommendations'>;

interface SummaryDetailsProps {
  appointment?: GetAppointmentStatuses;
  isPatientSkippedAppointment?: boolean;
}

const SummaryViewState: React.FC<SummaryDetailsProps> = ({ appointment }) => {
  const { user } = useAuth();
  const isProvider = user?.role === Roles.PROVIDER;
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.COMPLETE_MY_VISIT_SUMMARY_MODAL,
  });

  const getMedia = useGetEncounterSignedUrlFromStorageFunction();
  const { fetchPDFFileByLink } = useGetFileFromStorage();

  const data = useMemo(
    () => ({
      files: (appointment?.encounterNotes?.files || []).map((item) => {
        return {
          fileId: item.url,
          name: item.title,
          url: item.url,
          size: item?.size?.toString(),
          type: item.contentType,
        };
      }) as FileItemType[],
      encounterId: appointment?.encounterId,
      summaryDiagnosis: appointment?.encounterNotes?.providerSummary || '',
      aiNotes: appointment?.encounterNotes?.systemGenerated || '',
      appointmentNotes: appointment?.encounterNotes?.providerNotes || '',
      lastUpdated: appointment?.encounterNotes?.lastUpdated,
      followUpRecommendations:
        appointment?.encounterNotes?.followupRecommendations || '',
    }),
    [appointment],
  );

  const handleDownloadFile = async (url?: string, filename?: string) => {
    if (!url || !data.encounterId) return;
    fetchPDFFileByLink(url);
    const publicUrl = await getMedia(url as string, data.encounterId);

    if (!publicUrl) return;

    downloadFileFromUrl(publicUrl, filename);
  };

  const isTranscriptionsRemovedDate = moment(appointment?.end)
    .add(30, 'days')
    .isBefore();

  if (loading || !locale) {
    return <Loader />;
  }

  return (
    <Card
      title={locale.summaryDetails}
      titleClassName="flex text-h4 text-btn-black font-semibold"
      showSeparator
      defaultExpanded
      subtitleClassName="!text-base font-medium max-desktop:mt-2.5"
      subtitle={moment(data.lastUpdated).format(
        `[Summary date:] ${STANDARD_DATE}`,
      )}
    >
      <div className="w-full flex flex-col gap-30 ">
        <div className={twMerge('w-full', 'hidden', isProvider && 'block')}>
          <div className="flex justify-between items-center">
            <label className="justify-between w-full whitespace-pre-wrap text-base text-btn-black font-semibold">
              {locale.aiNotes}
            </label>
            <p
              className={twMerge(
                'text-xs text-med-gray-3 font-semibold whitespace-nowrap',
                isTranscriptionsRemovedDate && 'hidden',
              )}
            >
              {locale?.generatedByVonage ?? 'Generated by Vonage'}
            </p>
          </div>
          {isTranscriptionsRemovedDate && !data.aiNotes && (
            <Alert
              type="info-new"
              desktopItemsAlign="desktop:items-start mt-2.5"
              customContent={
                <div className="-mt-1">
                  <p className="text-neutral-800 font-semibold">
                    AI transcript no longer available
                  </p>
                  <p className="text-med-gray-3 font-medium">
                    AI-generated transcripts are temporary and are automatically
                    deleted 30 days after the visit
                  </p>
                </div>
              }
            />
          )}
          <div
            className={twMerge(
              'text-med-gray-3 font-medium',
              isTranscriptionsRemovedDate && 'hidden',
            )}
          >
            {data.aiNotes || '–'}
          </div>
        </div>

        <div className={twMerge('w-full', 'hidden', isProvider && 'block')}>
          <label className="justify-between w-full text-base text-btn-black font-semibold">
            {locale.youSummaryNotes}
          </label>

          <div className="break-all text-med-gray-3 whitespace-pre-wrap  font-medium">
            {data.appointmentNotes || '–'}
          </div>
        </div>

        <hr className={twMerge('hidden', isProvider && 'block')} />

        <div className="w-full">
          <label className="justify-between w-full text-base text-btn-black font-semibold">
            {locale.yourSummaryDiagnosis}
          </label>
          <div className="break-all text-med-gray-3 whitespace-pre-wrap font-medium">
            {data.summaryDiagnosis || '–'}
          </div>
        </div>

        <div className="w-full">
          <label className="justify-between w-full text-base text-btn-black font-semibold">
            {locale.followUpRecommendations}
          </label>
          <div className="break-all text-med-gray-3 whitespace-pre-wrap font-medium">
            {data.followUpRecommendations || '–'}
          </div>
        </div>

        {!!data.files.length && (
          <>
            <hr />
            <div>
              <label className="justify-between mb-0.5 w-full text-base text-btn-black font-semibold">
                {isProvider
                  ? locale?.additionalResourcesAndInformation
                  : 'Additional information and resources from your provider'}
              </label>
            </div>
          </>
        )}

        <div
          className={twMerge(
            'flex flex-col gap-2.5 w-full',
            !data.files.length && 'hidden',
          )}
        >
          {data.files.map((file) => {
            const formatFile = {
              ...file,
              name: file.name,
              type: file.type ? mapApiFileType[file.type] : '',
              size: formatFileSize(Number(file.size) || 0),
            };

            return (
              <FileItem
                file={formatFile}
                onDownloadFile={handleDownloadFile}
                key={file.fileId}
                isEditMode={false}
                encounterId={''}
              />
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default SummaryViewState;
